import { ReactElement } from 'react';

import { rem, Flex, Box, Button } from '@vimeo/bokeh';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';
import { Optional } from '@vimeo/core/types/generic';

import { createDomName } from '../../../utils/dom/identifying';

interface IEmptyStatePlaceholderProps extends IBaseDomElementProps {
  isWithScroll?: boolean;
  isWithPadding?: boolean;
  description?: string;
  icon?: ReactElement;
  flexGrow?: number;
  height?: string;
  /**
   * Custom control for overriding of bottom section.
   * If not provided (undefined), uses default button.
   * In other cases consumes provided component and uses it as bottom control.
   */
  control?: Optional<ReactElement> | boolean;
  buttonLeftIcon?: ReactElement;
  buttonLabel?: string;
  onButtonClick?: () => void;
}

/**
 * Shared empty state for different interaction/tools elements.
 */
export function EmptyStatePlaceholder({
  id = createDomName('empty-tab-placeholder'),
  className = createDomName('empty-tab-placeholder'),
  isWithScroll = false,
  isWithPadding = false,
  icon,
  control,
  buttonLabel,
  buttonLeftIcon,
  description,
  onButtonClick,
  flexGrow = 1,
  height = '100%',
}: IEmptyStatePlaceholderProps) {
  let paddingValue = '0';

  if (isWithPadding && isWithScroll) {
    paddingValue = `${rem(24)} ${rem(48)} ${rem(24)} ${rem(24)}`;
  } else if (isWithPadding) {
    paddingValue = rem(24);
  } else if (isWithScroll) {
    paddingValue = `0 ${rem(20)} 0 0`;
  }

  return (
    <Flex
      id={id}
      className={className}
      alignItems={'center'}
      justifyContent={'center'}
      flexDirection={'column'}
      flexGrow={flexGrow}
      height={height}
      gap={rem(16)}
      padding={paddingValue}
    >
      <Flex
        id={createDomName(id, 'icon')}
        className={createDomName(className, 'icon')}
        alignItems={'flex-end'}
        justifyContent={'center'}
      >
        {icon}
      </Flex>

      {description ? (
        <Box
          id={createDomName(id, 'description')}
          className={createDomName(className, 'description')}
          fontSize={'body-md'}
          color={'text-secondary'}
          textAlign={'center'}
        >
          {description}
        </Box>
      ) : null}

      {control !== true && (control || control === null || control === false) ? (
        control
      ) : (
        <Button
          id={createDomName(id, 'button')}
          className={createDomName(id, 'button')}
          size={'sm'}
          variant={'primary'}
          leftIcon={buttonLeftIcon}
          onClick={onButtonClick}
        >
          {buttonLabel}
        </Button>
      )}
    </Flex>
  );
}
